import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faTimes, faCamera } from '@fortawesome/free-solid-svg-icons';
import { reviewsAPI } from '../services/api';
import './ReviewForm.css';

const ReviewForm = ({ productId, orderId, eligibleOrders, productName, onSuccess, onCancel }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState(orderId || '');

  const ratingLabels = ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 3) {
      setError('You can upload up to 3 images');
      return;
    }

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImages((prev) => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    if (!comment.trim()) {
      setError('Please write a review');
      return;
    }

    if (comment.length > 500) {
      setError('Review must be 500 characters or less');
      return;
    }

    // Check if orderId is provided or selected
    const finalOrderId = selectedOrderId || orderId;
    if (!finalOrderId) {
      setError('You can only review products you have purchased. Please complete a purchase first.');
      return;
    }

    try {
      setLoading(true);
      await reviewsAPI.createReview({
        productId,
        orderId: finalOrderId,
        rating,
        title: title.trim(),
        comment: comment.trim(),
        images
      });

      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.message || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="AutoboyExpress-review-form-container">
      <div className="AutoboyExpress-review-form-header">
        <h3>Write a Review</h3>
        {productName && <p className="AutoboyExpress-review-form-product">{productName}</p>}
      </div>

      {error && (
        <div className="AutoboyExpress-review-form-error">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="AutoboyExpress-review-form">
        {eligibleOrders && eligibleOrders.length > 0 && !orderId && (
          <div className="AutoboyExpress-review-form-section">
            <label className="AutoboyExpress-review-form-label">Select Your Order *</label>
            <select
              className="AutoboyExpress-review-form-select"
              value={selectedOrderId}
              onChange={(e) => setSelectedOrderId(e.target.value)}
              required
            >
              <option value="">Choose an order...</option>
              {eligibleOrders.map((order) => (
                <option key={order._id || order.id} value={order._id || order.id}>
                  Order #{order.order_number || order._id?.slice(-6) || order.id?.slice(-6)} - {new Date(order.created_at || order.createdAt).toLocaleDateString()}
                </option>
              ))}
            </select>
          </div>
        )}

        {eligibleOrders && eligibleOrders.length === 0 && !orderId && (
          <div className="AutoboyExpress-review-form-notice">
            You must have a completed order for this product to write a review.
          </div>
        )}

        <div className="AutoboyExpress-review-form-section">
          <label className="AutoboyExpress-review-form-label">Your Rating *</label>
          <div className="AutoboyExpress-review-form-stars">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                className="AutoboyExpress-review-star-btn"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
              >
                <FontAwesomeIcon
                  icon={faStar}
                  className={star <= (hoverRating || rating) ? 'star-filled' : 'star-empty'}
                />
              </button>
            ))}
          </div>
          {rating > 0 && (
            <p className="AutoboyExpress-review-rating-label">{ratingLabels[rating - 1]}</p>
          )}
        </div>

        <div className="AutoboyExpress-review-form-section">
          <label className="AutoboyExpress-review-form-label">Review Title (Optional)</label>
          <input
            type="text"
            className="AutoboyExpress-review-form-input"
            placeholder="Summarize your experience..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={100}
          />
        </div>

        <div className="AutoboyExpress-review-form-section">
          <label className="AutoboyExpress-review-form-label">Your Review *</label>
          <textarea
            className="AutoboyExpress-review-form-textarea"
            placeholder="Share your experience with this product..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={6}
            maxLength={500}
          />
          <div className="AutoboyExpress-review-char-count">
            {comment.length}/500
          </div>
        </div>

        <div className="AutoboyExpress-review-form-section">
          <label className="AutoboyExpress-review-form-label">Photos (Optional)</label>
          <div className="AutoboyExpress-review-form-images">
            {images.map((img, index) => (
              <div key={index} className="AutoboyExpress-review-image-preview">
                <img src={img} alt={`Preview ${index + 1}`} />
                <button
                  type="button"
                  className="AutoboyExpress-review-image-remove"
                  onClick={() => removeImage(index)}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            ))}
            {images.length < 3 && (
              <label className="AutoboyExpress-review-image-upload">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
                <FontAwesomeIcon icon={faCamera} />
                <span>Add Photo</span>
              </label>
            )}
          </div>
          <p className="AutoboyExpress-review-image-hint">You can upload up to 3 photos</p>
        </div>

        <div className="AutoboyExpress-review-form-actions">
          <button
            type="button"
            className="AutoboyExpress-review-form-cancel"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="AutoboyExpress-review-form-submit"
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit Review'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReviewForm;
